<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import AppIcon from '@/components/AppIcon.vue'
import moment from 'moment'
import { axios } from '@/utils/axios'
import { useBackgroundActions } from '@/composables/useBackgroundActions'

const props = defineProps({
    actionId: {
        type: Number,
        required: true,
    },
})

const emit = defineEmits(['closeToast'])

const store = useStore()
const router = useRouter()
const { removeBackgroundAction, stopPolling } = useBackgroundActions()

// Get action reactively from store
const action = computed(() => store.getters.getBackgroundActionById(props.actionId))

const duration = computed(() => {
    if (!action.value) return '0s'
    const start = moment(action.value.startTime)
    const end = action.value.endTime ? moment(action.value.endTime) : moment()
    const diff = moment.duration(end.diff(start))
    const seconds = Math.floor(diff.asSeconds())
    if (seconds < 60) {
        return `${seconds}s`
    }
    return diff.humanize()
})

const navigateToResult = () => {
    if (action.value?.navigateToQueryParams) {
        const current = router.currentRoute.value
        const { href } = router.resolve({
            path: current.path,
            query: { ...current.query, ...action.value.navigateToQueryParams },
        })
        removeBackgroundAction(props.actionId)
        emit('closeToast')
        window.location.href = href
    } else if (action.value?.navigateTo) {
        const { href } = router.resolve(action.value.navigateTo)
        removeBackgroundAction(props.actionId)
        emit('closeToast')
        window.location.href = href
    }
}

const cancelAction = async () => {
    if (action.value?.cancelUrl) {
        try {
            await axios.post(action.value.cancelUrl)
        } catch (e) {
            console.warn('Could not cancel action on backend:', e?.message)
        }
    }
    stopPolling(props.actionId)
    removeBackgroundAction(props.actionId)
    emit('closeToast')
}

const closeToast = () => {
    removeBackgroundAction(props.actionId)
    emit('closeToast')
}
</script>

<template>
    <div
        v-if="action"
        class="w-[356px] rounded-lg border bg-white shadow-lg"
        :class="{
            'border-slate-200': action.status === 'in-progress',
            'border-emerald-200': action.status === 'completed',
            'border-rose-200': action.status === 'failed',
        }"
    >
        <!-- Header row -->
        <div class="flex items-center justify-between px-4 pt-3 pb-2">
            <div class="flex items-center gap-3">
                <!-- Status icon -->
                <span v-if="action.status === 'completed'" class="text-emerald-500">
                    <AppIcon name="check-circle" class="h-5 w-5" />
                </span>
                <span v-else-if="action.status === 'failed'" class="text-rose-500">
                    <AppIcon name="exclamation-circle" class="h-5 w-5" />
                </span>
                <span v-else class="text-indigo-500">
                    <AppIcon name="spinner-third" class="h-5 w-5 animate-spin" />
                </span>
                <!-- Title -->
                <span class="text-sm font-semibold text-slate-800">{{ action.description }}</span>
            </div>
            <!-- Close button -->
            <button
                class="rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                @click="closeToast"
            >
                <AppIcon name="times" class="h-3.5 w-3.5" />
            </button>
        </div>

        <!-- Progress section (in-progress only) -->
        <div v-if="action.status === 'in-progress'" class="px-4 pb-3">
            <!-- Progress message -->
            <div class="mb-2 flex items-center justify-between text-xs text-slate-500">
                <span>{{ action.progressMessage || 'Processing...' }}</span>
                <span>{{ action.progress || 0 }}%</span>
            </div>
            <!-- Progress bar -->
            <div class="h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
                <div
                    class="h-full bg-indigo-500 transition-all duration-300 ease-out"
                    :style="{ width: `${action.progress || 0}%` }"
                ></div>
            </div>
            <!-- Duration and Cancel -->
            <div class="mt-2 flex items-center justify-between">
                <span class="text-xs text-slate-400">Running for {{ duration }}</span>
                <button
                    v-if="action.cancelUrl"
                    class="rounded bg-slate-500 px-2 py-0.5 text-xs font-medium text-white transition hover:bg-slate-600"
                    @click="cancelAction"
                >
                    <AppIcon name="times" class="mr-1 h-2.5 w-2.5" />
                    Cancel
                </button>
            </div>
        </div>

        <!-- Completed section -->
        <div v-else-if="action.status === 'completed'" class="px-4 pb-3">
            <p v-if="action.statusMessage" class="mb-1 text-xs text-emerald-600">
                {{ action.statusMessage }}
            </p>
            <div class="flex items-center justify-between">
                <span class="text-xs text-slate-500">Completed in {{ duration }}</span>
                <div class="flex items-center gap-2">
                    <!-- View result button -->
                    <button
                        v-if="action.navigateTo || action.navigateToQueryParams"
                        class="rounded bg-indigo-500 px-3 py-1 text-xs font-medium text-white transition hover:bg-indigo-600"
                        @click="navigateToResult"
                    >
                        View
                    </button>
                </div>
            </div>
        </div>

        <!-- Failed section -->
        <div v-else-if="action.status === 'failed'" class="px-4 pb-3">
            <div class="mb-2 text-xs text-rose-600">
                {{ action.error || action.statusMessage || 'An error occurred' }}
            </div>
            <div class="flex items-center justify-between">
                <span class="text-xs text-slate-400">Failed after {{ duration }}</span>
                <div v-if="action.navigateTo || action.navigateToQueryParams" class="flex items-center gap-2">
                    <!-- Retry button -->
                    <button
                        v-if="action.navigateTo || action.navigateToQueryParams"
                        class="rounded bg-indigo-500 px-3 py-1 text-xs font-medium text-white transition hover:bg-indigo-600"
                        @click="navigateToResult"
                    >
                        Retry
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>
